import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Heart,
  Lightbulb,
  RotateCcw,
  LogOut,
  Repeat,
  ShieldAlert,
  Trophy,
  User,
  Users,
  XCircle,
  Zap,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GhsPictogramIcon from "@/games/labSafety/GhsPictogramIcon";
import {
  BUZZER_QUESTIONS,
  BUZZER_TURN_SECONDS,
  SAFETY_TURN_SECONDS,
  STARTING_LIVES,
  generateSafetyQuestion,
  scoreBuzzerAnswer,
  scoreSafetyAnswer,
} from "@/games/labSafety/engine";
import type { SafetyQuestion } from "@/games/labSafety/questions";

const SOLO_BEST_KEY = "elementpad:lab-safety:best";
const RESULT_PAUSE_MS = 2200;

type Mode = "solo" | "hot-seat" | "buzzer";

/**
 * One row in the match history. Used to build the end-of-match recap:
 * counts, kind breakdown, and the "top lessons" list pulled from
 * questions the player got wrong (or timed out on).
 */
type HistoryEntry = {
  question: SafetyQuestion;
  outcome: "correct" | "wrong" | "timeout";
  /** Hot-seat / buzzer only. null in solo / when nobody answered. */
  player?: PlayerId | null;
};

/**
 * Lab Safety Showdown - three modes:
 *  - Solo: lives-based survival; how many questions can you clear with 3 hearts?
 *  - Hot-seat 2P: alternating turns, 3 lives each, last standing wins.
 *  - Buzzer race: shared screen; P1 keys A/S/D/F vs P2 keys J/K/L/; - first
 *    correct keypress wins points, wrong locks the player out for the round.
 */
export default function LabSafetyShowdown() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode | null>(null);

  if (!mode) return <ModeSelect onPick={setMode} />;
  const exit = () => navigate("/play");
  const change = () => setMode(null);
  if (mode === "solo") return <SoloMatch onChangeMode={change} onExit={exit} />;
  if (mode === "hot-seat") return <HotSeatMatch onChangeMode={change} onExit={exit} />;
  return <BuzzerMatch onChangeMode={change} onExit={exit} />;
}

// ---------------- mode select ----------------

function ModeSelect({ onPick }: { onPick: (m: Mode) => void }) {
  return (
    <GameLayout
      title="Lab Safety Showdown"
      subtitle="Spot the hazard, pick the right action, recognise the symbol - before time runs out."
      headerActions={<GameMenu gameKey="lab-safety-showdown" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldAlert size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Scenarios, GHS pictograms, and PPE questions drawn from a shared bank.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <ModeCard
            icon={<User size={14} className="text-primary" aria-hidden="true" />}
            label="Solo"
            blurb={`Survive with ${STARTING_LIVES} ❤. Personal best on this device.`}
            onClick={() => onPick("solo")}
          />
          <ModeCard
            icon={<Users size={14} className="text-primary" aria-hidden="true" />}
            label="Hot-seat 2P"
            blurb={`Alternate turns, ${STARTING_LIVES} ❤ each. Last standing wins.`}
            onClick={() => onPick("hot-seat")}
          />
          <ModeCard
            icon={<Zap size={14} className="text-primary" aria-hidden="true" />}
            label="Buzzer race"
            blurb="Same screen. First correct keypress scores."
            onClick={() => onPick("buzzer")}
          />
        </div>
      </div>
    </GameLayout>
  );
}

function ModeCard({
  icon,
  label,
  blurb,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  blurb: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-foreground">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{blurb}</p>
    </button>
  );
}

// ---------------- solo (lives) ----------------

type SoloPhase =
  | { kind: "playing"; question: SafetyQuestion; lives: number; cleared: number }
  | {
      kind: "result";
      question: SafetyQuestion;
      chosenIndex: number | null;
      lives: number;
      cleared: number;
      outcome: "correct" | "wrong" | "timeout";
    }
  | { kind: "finished"; cleared: number; best: number; history: HistoryEntry[] };

const readBest = () => {
  try {
    return Number(window.localStorage.getItem(SOLO_BEST_KEY)) || 0;
  } catch {
    return 0;
  }
};
const writeBest = (n: number) => {
  try {
    window.localStorage.setItem(SOLO_BEST_KEY, String(n));
  } catch {
    /* ignore */
  }
};

function SoloMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const askedRef = useRef<Set<string>>(new Set());
  const advance = useRef<number | null>(null);
  const historyRef = useRef<HistoryEntry[]>([]);
  const [phase, setPhase] = useState<SoloPhase>(() => {
    const q = generateSafetyQuestion(Math.random, new Set());
    askedRef.current.add(q.id);
    return { kind: "playing", question: q, lives: STARTING_LIVES, cleared: 0 };
  });

  useEffect(() => () => {
    if (advance.current) window.clearTimeout(advance.current);
  }, []);

  const answer = useCallback(
    (chosenIndex: number | null) => {
      if (phase.kind !== "playing") return;
      const { outcome, livesDelta } = scoreSafetyAnswer(chosenIndex, phase.question);
      const newLives = Math.max(0, phase.lives + livesDelta);
      const newCleared = phase.cleared + (outcome === "correct" ? 1 : 0);
      if (outcome === "correct") play("correct");
      else play("wrong");

      historyRef.current.push({ question: phase.question, outcome });

      setPhase({
        kind: "result",
        question: phase.question,
        chosenIndex,
        lives: newLives,
        cleared: newCleared,
        outcome,
      });

      advance.current = window.setTimeout(() => {
        if (newLives <= 0) {
          const best = readBest();
          if (newCleared > best) writeBest(newCleared);
          play(newCleared > best ? "win" : "click");
          setPhase({
            kind: "finished",
            cleared: newCleared,
            best: Math.max(best, newCleared),
            history: historyRef.current.slice(),
          });
          return;
        }
        const q = generateSafetyQuestion(Math.random, askedRef.current);
        askedRef.current.add(q.id);
        setPhase({ kind: "playing", question: q, lives: newLives, cleared: newCleared });
      }, RESULT_PAUSE_MS);
    },
    [phase],
  );

  const onExpire = useCallback(() => answer(null), [answer]);

  const restart = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    historyRef.current = [];
    const q = generateSafetyQuestion(Math.random, new Set());
    askedRef.current.add(q.id);
    setPhase({ kind: "playing", question: q, lives: STARTING_LIVES, cleared: 0 });
  };

  if (phase.kind === "finished") {
    return (
      <MatchRecap
        title="Lab Safety Showdown"
        subtitle="Solo recap"
        headline={phase.cleared > 0 ? `Cleared ${phase.cleared} question${phase.cleared === 1 ? "" : "s"}` : "Out of lives"}
        sublabel={`Personal best: ${phase.best}`}
        livesRow={[{ label: "Lives remaining", value: 0, max: STARTING_LIVES }]}
        history={phase.history}
        onPrimary={restart}
        primaryLabel="Play again"
        onChangeMode={onChangeMode}
        onExit={onExit}
      />
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const lives = active?.lives ?? result!.lives;
  const cleared = active?.cleared ?? result!.cleared;

  return (
    <GameLayout
      title="Lab Safety Showdown"
      subtitle="Solo · 3 lives, no time to lose"
      headerActions={<GameMenu gameKey="lab-safety-showdown" onReset={restart} resetLabel="Restart" />}
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Lives" value={"❤".repeat(lives) + "·".repeat(STARTING_LIVES - lives)} />
        <Stat label="Cleared" value={String(cleared)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}-${cleared}`}
              seconds={SAFETY_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Badge variant="outline" className="text-[10px]">Reviewing</Badge>
          )}
        </div>
      </div>

      <SafetyQuestionCard
        question={q}
        chosenIndex={result?.chosenIndex ?? null}
        onChoose={(i) => active && answer(i)}
        revealed={!!result}
      />

      {result && (
        <SafetyResultBanner
          outcome={result.outcome}
          explanation={q.explanation}
          whyCorrect={q.whyCorrect}
        />
      )}
    </GameLayout>
  );
}

// ---------------- hot-seat (lives each) ----------------

type HotSeatPhase =
  | { kind: "answering"; question: SafetyQuestion; startedAt: number }
  | {
      kind: "result";
      question: SafetyQuestion;
      chosenIndex: number | null;
      outcome: "correct" | "wrong" | "timeout";
    }
  | { kind: "handoff"; nextPlayer: PlayerId };

function HotSeatMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const match = useHotSeatMatch(STARTING_LIVES);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const askedRef = useRef<Set<string>>(new Set());
  const advance = useRef<number | null>(null);
  const historyRef = useRef<HistoryEntry[]>([]);

  useEffect(() => () => {
    if (advance.current) window.clearTimeout(advance.current);
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      historyRef.current = [];
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateSafetyQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, []);

  const submit = useCallback(
    (chosenIndex: number | null) => {
      if (!phase || phase.kind !== "answering") return;
      const { outcome, livesDelta } = scoreSafetyAnswer(chosenIndex, phase.question);
      const current = match.state.currentTurn;
      const newLives = Math.max(0, match.state.stats[current] + livesDelta);
      match.setStat(current, newLives);
      if (outcome === "correct") play("correct");
      else play("wrong");

      historyRef.current.push({ question: phase.question, outcome, player: current });

      setPhase({ kind: "result", question: phase.question, chosenIndex, outcome });

      advance.current = window.setTimeout(() => {
        const otherLives = match.state.stats[current === 0 ? 1 : 0];
        if (newLives <= 0) {
          // current player out; other wins (or draw if both reach 0 simultaneously, unlikely here)
          const winner: PlayerId | null = otherLives <= 0 ? null : ((current === 0 ? 1 : 0) as PlayerId);
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match],
  );

  const onExpire = useCallback(() => submit(null), [submit]);

  const handleRematch = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Lab Safety Showdown"
        subtitle={`Hot-seat · ${STARTING_LIVES} lives each, last standing wins`}
        headerActions={<GameMenu gameKey="lab-safety-showdown" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players alternate turns. A wrong answer or timeout costs a life."
          ctaLabel="Start showdown"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];

  if (matchOver) {
    const winner =
      match.state.winner === null ? null : match.state.players[match.state.winner];
    return (
      <MatchRecap
        title="Lab Safety Showdown"
        subtitle="Hot-seat recap"
        headline={winner ? `${winner.name} wins!` : "It's a draw"}
        sublabel={`${STARTING_LIVES} lives each. A wrong answer or timeout costs one.`}
        livesRow={match.state.players.map((p) => ({
          label: p.name,
          value: match.state.stats[p.id],
          max: STARTING_LIVES,
        }))}
        history={historyRef.current}
        playerNames={[match.state.players[0].name, match.state.players[1].name]}
        onPrimary={handleRematch}
        primaryLabel="Rematch"
        onChangeMode={onChangeMode}
        onExit={onExit}
        secondaryAction={{ label: "Change players", onClick: handleChangePlayers }}
      />
    );
  }

  return (
    <GameLayout
      title="Lab Safety Showdown"
      subtitle="Hot-seat 2-player"
      headerActions={<GameMenu gameKey="lab-safety-showdown" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Lives" statMax={STARTING_LIVES} />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Lives left: <span className="font-mono">{match.state.stats[match.state.currentTurn]}</span>
          </p>
          <Button onClick={beginTurn} size="lg">
            <ShieldAlert size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <TurnTimer
              key={`hs-${phase.question.id}`}
              seconds={SAFETY_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <SafetyQuestionCard
            question={phase.question}
            chosenIndex={null}
            onChoose={submit}
            revealed={false}
          />
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <SafetyQuestionCard
            question={phase.question}
            chosenIndex={phase.chosenIndex}
            onChoose={() => undefined}
            revealed
          />
          <SafetyResultBanner
            outcome={phase.outcome}
            explanation={phase.question.explanation}
            whyCorrect={phase.question.whyCorrect}
          />
        </>
      )}
    </GameLayout>
  );
}

// ---------------- buzzer race ----------------

const P1_KEYS = ["a", "s", "d", "f"] as const;
const P2_KEYS = ["j", "k", "l", ";"] as const;

type BuzzerPhase =
  | { kind: "setup" }
  | { kind: "ready"; index: number }
  | {
      kind: "live";
      index: number;
      question: SafetyQuestion;
      startedAt: number;
      /** Players locked out for this question (after wrong guess). */
      lockedOut: Set<PlayerId>;
    }
  | {
      kind: "result";
      index: number;
      question: SafetyQuestion;
      winnerThisRound: PlayerId | null;
      chosenIndex: number | null;
      points: number;
      outcome: "fast" | "slow" | "wrong" | "timeout";
    }
  | { kind: "finished" };

function BuzzerMatch({ onChangeMode, onExit }: { onChangeMode: () => void; onExit: () => void }) {
  const match = useHotSeatMatch(0); // stats = points
  const [phase, setPhase] = useState<BuzzerPhase>({ kind: "setup" });
  const askedRef = useRef<Set<string>>(new Set());
  const advance = useRef<number | null>(null);
  const historyRef = useRef<HistoryEntry[]>([]);

  useEffect(() => () => {
    if (advance.current) window.clearTimeout(advance.current);
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      historyRef.current = [];
      match.start(p1, p2);
      setPhase({ kind: "ready", index: 0 });
    },
    [match],
  );

  const beginRound = useCallback(() => {
    if (phase.kind !== "ready") return;
    const q = generateSafetyQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({
      kind: "live",
      index: phase.index,
      question: q,
      startedAt: performance.now(),
      lockedOut: new Set(),
    });
  }, [phase]);

  const finishRound = useCallback(
    (
      live: Extract<BuzzerPhase, { kind: "live" }>,
      player: PlayerId | null,
      chosenIndex: number | null,
      outcome: "fast" | "slow" | "wrong" | "timeout",
      points: number,
    ) => {
      if (player !== null && points > 0) {
        match.setStat(player, match.state.stats[player] + points);
      }
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      else play("expire");

      // Map buzzer outcome → recap outcome.
      const recapOutcome: HistoryEntry["outcome"] =
        outcome === "fast" || outcome === "slow"
          ? "correct"
          : outcome === "timeout"
            ? "timeout"
            : "wrong";
      historyRef.current.push({
        question: live.question,
        outcome: recapOutcome,
        player: outcome === "fast" || outcome === "slow" ? player : null,
      });

      setPhase({
        kind: "result",
        index: live.index,
        question: live.question,
        winnerThisRound: player,
        chosenIndex,
        points,
        outcome,
      });

      advance.current = window.setTimeout(() => {
        const nextIndex = live.index + 1;
        if (nextIndex >= BUZZER_QUESTIONS) {
          const a = match.state.stats[0] + (player === 0 ? points : 0);
          const b = match.state.stats[1] + (player === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          setPhase({ kind: "finished" });
          return;
        }
        match.nextTurn();
        setPhase({ kind: "ready", index: nextIndex });
      }, RESULT_PAUSE_MS);
    },
    [match],
  );

  // Keyboard buzzer
  useEffect(() => {
    if (phase.kind !== "live") return;
    const handler = (e: KeyboardEvent) => {
      // Ignore if focus is on a form field
      const tgt = e.target as HTMLElement | null;
      if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA")) return;
      const key = e.key.toLowerCase();
      let player: PlayerId | null = null;
      let choice = -1;
      const i1 = P1_KEYS.indexOf(key as (typeof P1_KEYS)[number]);
      const i2 = P2_KEYS.indexOf(key as (typeof P2_KEYS)[number]);
      if (i1 >= 0) {
        player = 0;
        choice = i1;
      } else if (i2 >= 0) {
        player = 1;
        choice = i2;
      } else {
        return;
      }
      if (phase.lockedOut.has(player)) return;
      e.preventDefault();
      const elapsed = performance.now() - phase.startedAt;
      const result = scoreBuzzerAnswer(choice, phase.question, elapsed);
      if (result.outcome === "wrong") {
        // Lock this player out; if both locked, end the round with no winner.
        const newLocked = new Set(phase.lockedOut);
        newLocked.add(player);
        play("wrong");
        if (newLocked.size === 2) {
          finishRound(phase, null, choice, "wrong", 0);
        } else {
          setPhase({ ...phase, lockedOut: newLocked });
        }
        return;
      }
      finishRound(phase, player, choice, result.outcome, result.points);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, finishRound]);

  const onExpire = useCallback(() => {
    if (phase.kind !== "live") return;
    finishRound(phase, null, null, "timeout", 0);
  }, [phase, finishRound]);

  const handleRematch = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "ready", index: 0 });
  };
  const handleChangePlayers = () => {
    if (advance.current) window.clearTimeout(advance.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.reset();
    setPhase({ kind: "setup" });
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Lab Safety Showdown"
        subtitle={`Buzzer race · ${BUZZER_QUESTIONS} rounds, first correct keypress wins`}
        headerActions={<GameMenu gameKey="lab-safety-showdown" />}
      >
        <PlayerSetup
          title="Set up the buzzer match"
          description="Both players see the same question. P1 buzzes with A / S / D / F. P2 buzzes with J / K / L / ;"
          ctaLabel="Start race"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";

  if (matchOver) {
    const winner =
      match.state.winner === null ? null : match.state.players[match.state.winner];
    return (
      <MatchRecap
        title="Lab Safety Showdown"
        subtitle="Buzzer race recap"
        headline={winner ? `${winner.name} wins!` : "It's a draw"}
        sublabel={`${BUZZER_QUESTIONS} rounds. Higher score wins.`}
        livesRow={match.state.players.map((p) => ({
          label: p.name,
          value: match.state.stats[p.id],
        }))}
        history={historyRef.current}
        playerNames={[match.state.players[0].name, match.state.players[1].name]}
        onPrimary={handleRematch}
        primaryLabel="Rematch"
        onChangeMode={onChangeMode}
        onExit={onExit}
        secondaryAction={{ label: "Change players", onClick: handleChangePlayers }}
      />
    );
  }

  return (
    <GameLayout
      title="Lab Safety Showdown"
      subtitle="Buzzer race · shared screen"
      headerActions={<GameMenu gameKey="lab-safety-showdown" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase.kind === "ready" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Zap size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground mb-1">
            Round <span className="font-mono">{phase.index + 1}</span> of {BUZZER_QUESTIONS}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            <span className="font-mono">{match.state.players[0].name}</span>: A · S · D · F &nbsp;·&nbsp;
            <span className="font-mono">{match.state.players[1].name}</span>: J · K · L · ;
          </p>
          <Button onClick={beginRound} size="lg">
            <Zap size={14} className="mr-1.5" aria-hidden="true" />
            Start round
          </Button>
        </div>
      )}

      {phase.kind === "live" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">
              Round {phase.index + 1} / {BUZZER_QUESTIONS}
              {phase.lockedOut.size > 0 && (
                <Badge variant="outline" className="ml-2 text-[10px]">
                  {Array.from(phase.lockedOut)
                    .map((p) => match.state.players[p].name)
                    .join(", ")}{" "}
                  locked out
                </Badge>
              )}
            </span>
            <TurnTimer
              key={`buzzer-${phase.question.id}-${phase.index}`}
              seconds={BUZZER_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <SafetyQuestionCard
            question={phase.question}
            chosenIndex={null}
            onChoose={() => undefined}
            revealed={false}
            keyHints={{
              p1: P1_KEYS as readonly string[],
              p2: P2_KEYS as readonly string[],
              p1Name: match.state.players[0].name,
              p2Name: match.state.players[1].name,
            }}
          />
        </>
      )}

      {phase.kind === "result" && !matchOver && (
        <>
          <SafetyQuestionCard
            question={phase.question}
            chosenIndex={phase.chosenIndex}
            onChoose={() => undefined}
            revealed
          />
          <BuzzerResultBanner
            outcome={phase.outcome}
            points={phase.points}
            winner={phase.winnerThisRound !== null ? match.state.players[phase.winnerThisRound].name : null}
            explanation={phase.question.explanation}
            whyCorrect={phase.question.whyCorrect}
          />
        </>
      )}
    </GameLayout>
  );
}

// ---------------- shared sub-components ----------------

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SafetyQuestionCard({
  question,
  chosenIndex,
  onChoose,
  revealed,
  keyHints,
}: {
  question: SafetyQuestion;
  chosenIndex: number | null;
  onChoose: (i: number) => void;
  revealed: boolean;
  /** Optional buzzer-mode key hints rendered inside each choice. */
  keyHints?: { p1: readonly string[]; p2: readonly string[]; p1Name: string; p2Name: string };
}) {
  const kindLabel = useMemo(() => {
    switch (question.kind) {
      case "scenario-hazard":
        return "Scenario · spot the hazard";
      case "scenario-action":
        return "Scenario · pick the right action";
      case "ghs-pictogram":
        return "GHS pictogram";
      case "ppe-id":
        return "PPE identification";
    }
  }, [question.kind]);

  return (
    <div className="rounded-xl border border-primary/40 bg-card p-5 mb-3">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="text-[10px]">{kindLabel}</Badge>
      </div>

      {question.kind === "ghs-pictogram" && question.pictogram && (
        <div className="flex justify-center mb-4">
          <GhsPictogramIcon symbol={question.pictogram} size={120} />
        </div>
      )}

      {question.kind === "ppe-id" && question.ppeIcon && (
        <div className="text-center text-5xl mb-3" aria-hidden="true">
          {question.ppeIcon}
        </div>
      )}

      <h2 className="text-base font-semibold text-foreground mb-2 leading-snug">
        {question.prompt}
      </h2>
      {question.detail && (
        <p className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded mb-4 leading-relaxed">
          {question.detail}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="group" aria-label="Answer choices">
        {question.choices.map((choice, i) => {
          const isCorrect = i === question.correctIndex;
          const isChosen = i === chosenIndex;
          const stateClasses = revealed
            ? isCorrect
              ? "border-primary bg-primary/10 text-foreground"
              : isChosen
                ? "border-destructive/60 bg-destructive/10 text-foreground"
                : "border-border bg-background text-muted-foreground"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent/40";
          return (
            <button
              key={i}
              type="button"
              onClick={() => !revealed && onChoose(i)}
              disabled={revealed || !!keyHints}
              className={`text-left p-3 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stateClasses} disabled:cursor-default`}
            >
              <div className="flex items-start gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold bg-muted text-muted-foreground flex-shrink-0">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-sm flex-1">{choice}</span>
              </div>
              {keyHints && (
                <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span>
                    <span className="font-semibold text-foreground">{keyHints.p1Name}</span>:{" "}
                    <kbd className="px-1.5 py-0.5 rounded border border-border bg-background font-mono uppercase">
                      {keyHints.p1[i]}
                    </kbd>
                  </span>
                  <span>
                    <span className="font-semibold text-foreground">{keyHints.p2Name}</span>:{" "}
                    <kbd className="px-1.5 py-0.5 rounded border border-border bg-background font-mono uppercase">
                      {keyHints.p2[i]}
                    </kbd>
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SafetyResultBanner({
  outcome,
  explanation,
  whyCorrect,
}: {
  outcome: "correct" | "wrong" | "timeout";
  explanation: string;
  whyCorrect: string;
}) {
  const positive = outcome === "correct";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive ? "border-primary/40 bg-primary/5" : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : outcome === "timeout" ? (
          <AlertTriangle size={14} className="text-destructive" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "correct" && "Correct - life saved"}
          {outcome === "wrong" && "Wrong - −1 life"}
          {outcome === "timeout" && "Time's up - −1 life"}
        </span>
        {!positive && (
          <Badge variant="secondary" className="text-[10px] ml-auto">
            <Heart size={10} className="mr-1" aria-hidden="true" />
            −1
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{explanation}</p>
      <WhyCorrect text={whyCorrect} defaultOpen={!positive} />
    </div>
  );
}

function BuzzerResultBanner({
  outcome,
  points,
  winner,
  explanation,
  whyCorrect,
}: {
  outcome: "fast" | "slow" | "wrong" | "timeout";
  points: number;
  winner: string | null;
  explanation: string;
  whyCorrect: string;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive ? "border-primary/40 bg-primary/5" : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {positive && winner && `${winner} buzzed in correctly! +${points} pts`}
          {outcome === "wrong" && "Both players locked out - no points awarded"}
          {outcome === "timeout" && "Nobody buzzed in - no points awarded"}
        </span>
        {positive && (
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {outcome === "fast" ? "Speed bonus" : "Standard"}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{explanation}</p>
      <WhyCorrect text={whyCorrect} defaultOpen={!positive} />
    </div>
  );
}

function WhyCorrect({ text, defaultOpen = false }: { text: string; defaultOpen?: boolean }) {
  return (
    <details
      className="mt-2 group rounded-md border border-border/60 bg-background/40 open:bg-background/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer list-none px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
        <BookOpen size={12} className="text-primary" aria-hidden="true" />
        <span>Why this is correct</span>
        <ChevronDown
          size={12}
          className="ml-auto text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <p className="px-2.5 pb-2.5 pt-0.5 text-xs text-muted-foreground leading-relaxed">
        {text}
      </p>
    </details>
  );
}

// ---------------- end-of-match recap ----------------

const KIND_LABEL: Record<SafetyQuestion["kind"], string> = {
  "scenario-hazard": "Hazard scenarios",
  "scenario-action": "First-action scenarios",
  "ghs-pictogram": "GHS pictograms",
  "ppe-id": "PPE identification",
};

/**
 * Shared end-of-match recap. Used by all three modes - the differences
 * (winner banner, lives bar, score bar) are passed in as props so the layout
 * stays consistent.
 */
function MatchRecap({
  title,
  subtitle,
  headline,
  sublabel,
  livesRow,
  history,
  playerNames,
  onPrimary,
  primaryLabel,
  onChangeMode,
  onExit,
  secondaryAction,
}: {
  title: string;
  subtitle: string;
  headline: string;
  sublabel?: string;
  /** Per-player or single-row stat to show at the top. `max` enables a progress bar. */
  livesRow: Array<{ label: string; value: number; max?: number }>;
  history: HistoryEntry[];
  /** Provided in 2-player modes so per-player counts can be shown. */
  playerNames?: [string, string];
  onPrimary: () => void;
  primaryLabel: string;
  onChangeMode: () => void;
  onExit: () => void;
  /** Optional second outline button (e.g. "Change players"). */
  secondaryAction?: { label: string; onClick: () => void };
}) {
  const total = history.length;
  const correctCount = history.filter((h) => h.outcome === "correct").length;
  const wrongCount = history.filter((h) => h.outcome === "wrong").length;
  const timeoutCount = history.filter((h) => h.outcome === "timeout").length;
  const accuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  // Top lessons: prioritise wrong + timeout (those are the actual mistakes),
  // then fall back to correct ones if everything was answered right. Dedupe
  // by question id so the same question isn't surfaced twice in 2-player modes
  // when both players faced it (rare but possible across rematches).
  const seen = new Set<string>();
  const mistakes = history.filter((h) => h.outcome !== "correct");
  const corrects = history.filter((h) => h.outcome === "correct");
  const lessonPool = [...mistakes, ...corrects].filter((h) => {
    if (seen.has(h.question.id)) return false;
    seen.add(h.question.id);
    return true;
  });
  const lessons = lessonPool.slice(0, 3);

  // Per-kind breakdown - small chips so students see which area to revise.
  const kindBuckets = (Object.keys(KIND_LABEL) as Array<SafetyQuestion["kind"]>).map(
    (kind) => {
      const items = history.filter((h) => h.question.kind === kind);
      return {
        kind,
        total: items.length,
        correct: items.filter((h) => h.outcome === "correct").length,
      };
    },
  );

  return (
    <GameLayout title={title} subtitle={subtitle}>
      {/* Headline */}
      <div className="rounded-xl border border-border bg-card p-6 mb-4 text-center">
        <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
        <h2 className="text-2xl font-bold text-foreground mb-1">{headline}</h2>
        {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
      </div>

      {/* Lives / score row */}
      <div
        className={`grid gap-3 mb-4 ${livesRow.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}
        role="group"
        aria-label="Final standing"
      >
        {livesRow.map((row) => (
          <div
            key={row.label}
            className="rounded-lg border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-foreground truncate">
                {row.label}
              </span>
              {row.max !== undefined ? (
                <span
                  className="text-sm font-mono tabular-nums text-foreground"
                  aria-label={`${row.value} of ${row.max}`}
                >
                  {row.value} / {row.max}
                </span>
              ) : (
                <span className="text-base font-mono tabular-nums text-foreground">
                  {row.value}
                </span>
              )}
            </div>
            {row.max !== undefined && (
              <div className="flex items-center gap-1" aria-hidden="true">
                {Array.from({ length: row.max }).map((_, i) => (
                  <Heart
                    key={i}
                    size={14}
                    className={
                      i < row.value
                        ? "text-primary fill-primary"
                        : "text-muted-foreground/30"
                    }
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Breakdown */}
      <div className="rounded-xl border border-border bg-card p-5 mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Answer breakdown
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <BreakdownStat
            label="Total"
            value={total}
            tone="neutral"
            icon={<ShieldAlert size={14} aria-hidden="true" />}
          />
          <BreakdownStat
            label="Correct"
            value={correctCount}
            tone="positive"
            icon={<CheckCircle2 size={14} aria-hidden="true" />}
          />
          <BreakdownStat
            label="Wrong"
            value={wrongCount}
            tone="negative"
            icon={<XCircle size={14} aria-hidden="true" />}
          />
          <BreakdownStat
            label="Timed out"
            value={timeoutCount}
            tone="negative"
            icon={<AlertTriangle size={14} aria-hidden="true" />}
          />
        </div>

        {total > 0 && (
          <>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
              <span>Accuracy</span>
              <span className="font-mono">{accuracy}%</span>
            </div>
            <div
              className="h-1.5 w-full rounded-full bg-muted overflow-hidden"
              role="progressbar"
              aria-valuenow={accuracy}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Accuracy"
            >
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${accuracy}%` }}
              />
            </div>
          </>
        )}

        {/* Per-kind breakdown chips */}
        {kindBuckets.some((b) => b.total > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-border">
            {kindBuckets
              .filter((b) => b.total > 0)
              .map((b) => (
                <Badge
                  key={b.kind}
                  variant="outline"
                  className="text-[10px] font-mono"
                >
                  {KIND_LABEL[b.kind]}: {b.correct}/{b.total}
                </Badge>
              ))}
          </div>
        )}
      </div>

      {/* Top lessons */}
      {lessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5 mb-4">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb size={14} className="text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground">
              {mistakes.length > 0 ? "Top lessons to revise" : "Top takeaways"}
            </h3>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            {mistakes.length > 0
              ? "Pulled from the questions you missed - review these before next session."
              : "You aced everything. A quick recap of the key points anyway:"}
          </p>
          <ul className="space-y-2.5">
            {lessons.map((entry, i) => (
              <li
                key={`${entry.question.id}-${i}`}
                className="rounded-md border border-border/60 bg-background/40 p-3"
              >
                <div className="flex items-start gap-2 mb-1">
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                      entry.outcome === "correct"
                        ? "bg-primary/15 text-primary"
                        : "bg-destructive/15 text-destructive"
                    }`}
                    aria-label={
                      entry.outcome === "correct" ? "Correct" : "Missed"
                    }
                  >
                    {entry.outcome === "correct" ? "✓" : "✗"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground leading-snug">
                      {entry.question.prompt}
                      {playerNames && entry.player !== undefined && entry.player !== null && (
                        <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                          · {playerNames[entry.player]}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                      {entry.question.explanation}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={onExit}>
          <LogOut size={14} className="mr-1.5" aria-hidden="true" />
          Quit
        </Button>
        <Button variant="outline" onClick={onChangeMode}>
          <Repeat size={14} className="mr-1.5" aria-hidden="true" />
          Change mode
        </Button>
        {secondaryAction && (
          <Button variant="outline" onClick={secondaryAction.onClick}>
            <Users size={14} className="mr-1.5" aria-hidden="true" />
            {secondaryAction.label}
          </Button>
        )}
        <Button onClick={onPrimary}>
          <RotateCcw size={14} className="mr-1.5" aria-hidden="true" />
          {primaryLabel}
        </Button>
      </div>
    </GameLayout>
  );
}

function BreakdownStat({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: "neutral" | "positive" | "negative";
  icon: React.ReactNode;
}) {
  const toneClasses =
    tone === "positive"
      ? "border-primary/30 bg-primary/5 text-primary"
      : tone === "negative"
        ? "border-destructive/30 bg-destructive/5 text-destructive"
        : "border-border bg-background text-muted-foreground";
  return (
    <div className={`rounded-md border p-3 ${toneClasses}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-xl font-bold font-mono tabular-nums text-foreground">
        {value}
      </div>
    </div>
  );
}